/**
 * Pure reducer: (ContactRecord, ContactEvent) → ContactRecord.
 * Also derives the side-effect commands that result from the transition.
 */

import type {
  ContactRecord,
  ContactEvent,
  ContactCommand,
  ContactStage,
} from './types';
import { TERMINAL_STAGES } from './types';
import { KIND_POLICY, type KindPolicy } from './kindPolicy';

// ---------------------------------------------------------------------------
// Transition table
// ---------------------------------------------------------------------------

type TransitionKey = `${ContactStage}|${ContactEvent['type']}`;

const TABLE: Partial<Record<TransitionKey, ContactStage>> = {
  // pending → …
  'pending|delivered': 'pending', // classification happens before reduce
  'pending|state_incoming': 'incoming',
  'pending|accepted': 'accepted',
  'pending|connected': 'connected',
  'pending|acw': 'afterCallWork',
  'pending|ended': 'ended',
  'pending|missed': 'missed',
  'pending|error': 'missed',
  'pending|destroyed': 'destroyed',

  // incoming → …
  'incoming|accepted': 'accepted',
  'incoming|connected': 'connected',
  'incoming|acw': 'afterCallWork',
  'incoming|ended': 'ended',
  'incoming|missed': 'missed',
  'incoming|error': 'missed',
  'incoming|destroyed': 'destroyed',

  // accepted → …
  'accepted|connected': 'connected',
  'accepted|acw': 'afterCallWork',
  'accepted|ended': 'ended',
  'accepted|missed': 'missed',
  'accepted|error': 'missed',
  'accepted|destroyed': 'destroyed',

  // connected → …
  'connected|acw': 'afterCallWork',
  'connected|ended': 'ended',
  'connected|missed': 'missed',
  'connected|error': 'missed',
  'connected|destroyed': 'destroyed',

  // afterCallWork → … (terminal, but can still end/destroy)
  'afterCallWork|ended': 'afterCallWork',
  'afterCallWork|destroyed': 'destroyed',

  // ended → … (terminal)
  'ended|destroyed': 'destroyed',

  // missed → … (terminal)
  'missed|destroyed': 'destroyed',
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function reduceContact(
  record: ContactRecord,
  event: ContactEvent
): ContactRecord {
  // Terminal stages reject everything except 'destroyed'.
  if (TERMINAL_STAGES.has(record.stage) && event.type !== 'destroyed') {
    return record;
  }

  const key = `${record.stage}|${event.type}` as TransitionKey;
  const nextStage = TABLE[key];

  if (nextStage === undefined) {
    // No transition defined — stay in current stage.
    return record;
  }

  return { ...record, stage: nextStage };
}

// ---------------------------------------------------------------------------
// Command derivation
// ---------------------------------------------------------------------------

/**
 * Derive commands from a state transition.
 * Called AFTER reduceContact with the previous and next records.
 */
export function deriveCommands(
  prev: ContactRecord,
  next: ContactRecord
): ContactCommand[] {
  const policy: KindPolicy = KIND_POLICY[next.kind];
  const cmds: ContactCommand[] = [];

  // -- terminal-stage cleanup --
  if (next.stage === 'missed') {
    cmds.push({ op: 'clearConnectContact', id: next.id });
    cmds.push({ op: 'clearContactStream', callScopeId: next.callScopeId });
    cmds.push({ op: 'removeFromActive', id: next.id });
    // An error/missed event after the agent already accepted means the call
    // died mid-answer (customer decline, ringtone stop) — the agent did not
    // miss anything, so keep them routable. Outbound calls are agent-initiated,
    // so the agent never "misses" them — never force not-routable.
    const agentWasEngaged =
      prev.stage === 'accepted' || prev.stage === 'connected';
    if (policy.setNotRoutableOnMissed && !agentWasEngaged && next.inbound) {
      cmds.push({ op: 'setAgentNotRoutable' });
    }
    return cmds;
  }

  if (next.stage === 'destroyed' || next.stage === 'afterCallWork') {
    cmds.push({ op: 'removeFromActive', id: next.id });
  }

  if (next.stage === 'afterCallWork') {
    cmds.push({ op: 'setAfterCallWork', id: next.id });
    return cmds;
  }

  if (next.stage === 'destroyed') {
    return cmds;
  }

  // -- ended: voice keeps session, chat → ACW --
  if (next.stage === 'ended') {
    if (policy.routeToAcwOnEnded) {
      cmds.push({ op: 'setAfterCallWork', id: next.id });
    }
    cmds.push({ op: 'removeFromActive', id: next.id });
    return cmds;
  }

  // -- incoming: show modal + fetch metadata --
  if (prev.stage !== 'incoming' && next.stage === 'incoming') {
    if (policy.showIncomingModal) {
      cmds.push({ op: 'setIncoming', id: next.id });
    }
    cmds.push({ op: 'fetchMetadata', callScopeId: next.callScopeId });
    // Auto-accept non-inbound contacts (except dialpad outbound calls).
    if (
      !next.inbound &&
      !next.dialpadOutbound &&
      next.kind !== 'monitor' &&
      policy.autoAcceptOnDelivery
    ) {
      cmds.push({ op: 'autoAcceptContact', id: next.id });
    }
  }

  // -- accepted --
  if (prev.stage !== 'accepted' && next.stage === 'accepted') {
    if (!policy.keepModalOnAccept) {
      cmds.push({ op: 'clearIncoming' });
      cmds.push({ op: 'clearContactInformation' });
    }
    cmds.push({
      op: 'registerSession',
      callScopeId: next.callScopeId,
      sessionType: next.kind === 'chat' ? 'chat' : 'voice',
      startedAt: Date.now(),
      focus: true,
    });
  }

  // -- connected --
  if (prev.stage !== 'connected' && next.stage === 'connected') {
    cmds.push({ op: 'clearIncoming' });
    cmds.push({ op: 'clearContactInformation' });
    // Start the transcript stream for every voice contact on connect.
    // Inbound voice normally opens the stream in the accept handler; emitting
    // it here as well acts as a fallback when the accept callback missed.
    // callService skips re-initiating when a stream is already open, so the
    // accept-handler stream (and warm-consult streams) are never clobbered.
    const shouldStream =
      (next.kind === 'voice' || next.kind === 'callback') &&
      !next.streamInitiated;
    if (shouldStream) {
      cmds.push({ op: 'initiateStream', callScopeId: next.callScopeId });
    }
    if (next.kind === 'chat') {
      cmds.push({ op: 'handleChatAccepted', id: next.id });
    }
  }

  return cmds;
}

// ---------------------------------------------------------------------------
// Combined: reduce + derive
// ---------------------------------------------------------------------------

export function transition(
  record: ContactRecord,
  event: ContactEvent
): { record: ContactRecord; commands: ContactCommand[] } {
  const next = reduceContact(record, event);
  const commands = deriveCommands(record, next);

  // Mark stream as initiated so the next transition won't emit it again.
  const hasStreamCmd = commands.some(c => c.op === 'initiateStream');
  const finalRecord = hasStreamCmd ? { ...next, streamInitiated: true } : next;

  return { record: finalRecord, commands };
}
